import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { timeAgo } from '../lib/timeAgo';
import { useToast } from '../lib/useToast';
import { ConfirmModal } from '../components/ConfirmModal';
import { PageHeader } from '../ui/PageHeader';
import { SkeletonRows } from '../ui/Skeleton';
import { ErrorBanner } from '../ui/ErrorBanner';

/**
 * ── FEATURES — turn product surfaces on and off without shipping an update ────────────────────
 *
 * Evan, 2026-10-27: *"maybe make me a new admin dash page in app management called 'Features'
 * where i toggle certain features on and off in general."*
 *
 * ⚠️ 🚨 THE PHONE DOES NOT READ THIS YET, AND THE PAGE SAYS SO IN A BANNER RATHER THAN PRETENDING.
 * Every flag in the app is a `const` compiled into the JS bundle (`lib/featureFlags.ts`), so today
 * the only way to flip one is an OTA. What this page writes is the OVERRIDE LAYER those constants
 * will be resolved against once the client-side read lands. Until then a toggle here is a STAGED
 * decision, not a live one — and a wall of switches that silently do nothing would be worse than
 * no page at all, which is why the banner is not dismissible.
 *
 * ⚠️ THE STORED SHAPE IS SPARSE ON PURPOSE: `{ "FEATURE_X": true }` holds ONLY the flags Evan has
 * explicitly overridden. A flag that is absent means "use whatever the app shipped with". Writing
 * the full set instead would freeze today's defaults into the database, so a later OTA that
 * deliberately changes a default would be silently reverted by a row nobody remembered setting.
 *
 * ⚠️ AND THAT IS ALSO THE SAFETY RULE FOR THE CLIENT HALF, WRITTEN HERE BECAUSE THIS IS WHERE
 * SOMEONE WILL LOOK: on a failed or empty read the app MUST fall back to the compiled constant,
 * never to `false`. One Supabase blip would otherwise turn every listed surface off at once, on
 * every phone, with no way to turn them back on except an update.
 *
 * ⚠️ TWO FLAGS ARE DELIBERATELY ABSENT FROM THIS PAGE (Evan approved both exclusions, K5):
 *   · `GOOGLE_PLACES_API_ENABLED` — an incident guard, not a product toggle. A mis-tap re-opens the
 *     unmetered spend path from INCIDENT_2026_05_19. It stays hardcoded.
 *   · `FEATURE_ANONYMOUS_BROWSE` — it decides who is allowed into the app at all, and a remote
 *     switch on that is a different category of risk from hiding a button.
 * `FEATURE_SEARCH_TAB` is also omitted: it is structural, not a surface.
 */

const CONFIG_KEY = 'feature_flags';

type Overrides = Record<string, boolean>;

type FlagSpec = {
  /** The exact `export const` name in `lib/featureFlags.ts` — this is the storage key. */
  key: string;
  label: string;
  blurb: string;
  /** What the CURRENT bundle ships with. Keep in step with `lib/featureFlags.ts`. */
  shipped: boolean;
  group: 'Shipping off' | 'Live surfaces' | 'Experiments';
  /** Shown as a warning line where flipping this is not a simple show/hide. */
  caveat?: string;
};

/**
 * ⚠️ `shipped` IS A MIRROR OF `lib/featureFlags.ts`, NOT A SOURCE OF TRUTH, and it can drift.
 * It exists so the page can show "override differs from what the app shipped with", which is the
 * single most useful thing on the screen. When a default changes in an OTA, update it here too.
 * Verified against the r86 bundle (`e587ca2`) on 2026-10-27.
 */
const FLAGS: FlagSpec[] = [
  {
    key: 'FEATURE_STORY_CARD_EXPORT',
    label: 'Story card export',
    blurb: 'Share-to-story button on your own feed posts.',
    shipped: false,
    group: 'Shipping off',
    caveat: 'Had no switch at all until 2026-10-27 and would have shipped in r86 by default.',
  },
  {
    key: 'FEATURE_PARTNER_WALLET_UI',
    label: 'Discount wallet and passes',
    blurb: 'Corner wallet button on all four tabs, the wallet sheet, the search claim card and the venue offer tag.',
    shipped: false,
    group: 'Shipping off',
    caveat: 'Also gates the wallet request that used to fire on every app launch — off costs nothing on the wire.',
  },
  {
    key: 'FEATURE_QR_ENABLED',
    label: 'QR connect',
    blurb: 'Show QR / Scan QR in the profile Connect menu.',
    shipped: false,
    group: 'Shipping off',
    caveat: 'Parked in June pending a reliability pass on swallowed follow errors and a navigation race.',
  },
  {
    key: 'FEATURE_SHOP_AND_REFERRALS_UI',
    label: 'Shado Shop and points',
    blurb: 'The Shop row on the profile and the points balance modal.',
    shipped: false,
    group: 'Shipping off',
  },
  {
    key: 'FEATURE_REFERRER_PYMK',
    label: 'Referrer atop People You May Know',
    blurb: 'Puts whoever invited you first in the suggestions, labelled "Referred you".',
    shipped: true,
    group: 'Live surfaces',
  },
  {
    key: 'FEATURE_MAP_SEARCH_SHORTCUT',
    label: 'Search shortcut on the map',
    blurb: 'The search disc in the Discover map’s top right that jumps to a focused search bar.',
    shipped: true,
    group: 'Live surfaces',
    caveat: 'Hidden while signed out regardless of this switch — the Create Account pill overlaps that slot by 41pt.',
  },
  {
    key: 'FEATURE_GOING_TONIGHT',
    label: 'Are you going tonight?',
    blurb: 'The head-count row on the venue page.',
    shipped: true,
    group: 'Live surfaces',
    caveat: 'On, but invisible: visibility is per city and no city is enabled. Turning this off is the kill switch for all of them at once.',
  },
  {
    key: 'FEATURE_GLASS_ON_RATING_CARDS',
    label: 'Glass on rating cards',
    blurb: 'Blur behind past-rating cards.',
    shipped: true,
    group: 'Experiments',
    caveat: 'Each blur is a live native view; this repo has a crash on record at 150+ of them in one list.',
  },
  {
    key: 'FEATURE_PEOPLE_UNDERLINE_TABS',
    label: 'Underline tabs on People',
    blurb: 'Moving underline instead of the pill row.',
    shipped: true,
    group: 'Experiments',
  },
  {
    key: 'FEATURE_PROFILE_UNDERLINE_TABS',
    label: 'Underline tabs on your profile',
    blurb: 'The same treatment on the ratings filter strip.',
    shipped: false,
    group: 'Experiments',
  },
];

const GROUP_ORDER: FlagSpec['group'][] = ['Shipping off', 'Live surfaces', 'Experiments'];

/** Keep only real booleans keyed by a flag this page knows — anything else is ignored, not trusted. */
function coerceOverrides(value: unknown): Overrides {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const known = new Set(FLAGS.map((f) => f.key));
  const out: Overrides = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (known.has(k) && typeof v === 'boolean') out[k] = v;
  }
  return out;
}

type Pending = { key: string; label: string; next: boolean | null };

export function FeaturesScreen() {
  const { showError, showSuccess, toastNode } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<Overrides>({});
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcErr } = await supabase.rpc('admin_get_app_config', { p_key: CONFIG_KEY });
      if (rpcErr) throw rpcErr;
      const row = Array.isArray(data) ? data[0] : data;
      setOverrides(coerceOverrides(row?.value));
      setUpdatedAt(typeof row?.updated_at === 'string' ? row.updated_at : null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not load feature flags');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * ⚠️ WRITES THE WHOLE MAP EVERY TIME, because `admin_set_app_config` replaces the value rather
   * than merging it. Reading the current state from React and posting the merge is the only way to
   * avoid dropping the other nine flags on each toggle.
   */
  const commit = useCallback(
    async (key: string, label: string, next: boolean | null) => {
      setBusyKey(key);
      try {
        const value: Overrides = { ...overrides };
        if (next === null) delete value[key];
        else value[key] = next;
        const { error: rpcErr } = await supabase.rpc('admin_set_app_config', {
          p_key: CONFIG_KEY,
          p_value: value,
        });
        if (rpcErr) throw rpcErr;
        setOverrides(value);
        setUpdatedAt(new Date().toISOString());
        showSuccess(
          next === null
            ? `${label} — back to whatever the app shipped with`
            : `${label} staged ${next ? 'on' : 'off'}`,
        );
      } catch (e: unknown) {
        showError(e instanceof Error ? e.message : 'Could not save');
      } finally {
        setBusyKey(null);
        setPending(null);
      }
    },
    [overrides, showError, showSuccess],
  );

  const overriddenCount = useMemo(() => Object.keys(overrides).length, [overrides]);

  return (
    <div>
      <PageHeader
        title="Features"
        subtitle={
          updatedAt
            ? `${overriddenCount} override${overriddenCount === 1 ? '' : 's'} set · last changed ${timeAgo(updatedAt)}`
            : 'No overrides set — every surface is using what the app shipped with'
        }
        actions={
          <button
            type="button"
            className="admin-btn admin-btn-secondary"
            onClick={() => void load()}
            disabled={loading}
          >
            Refresh
          </button>
        }
      />

      {/* Not dismissible, and deliberately above everything: a switch that does nothing yet must say so. */}
      <div className="admin-card mb-6 border-l-4 border-amber-500">
        <div className="font-semibold">The app does not read this page yet</div>
        <p className="admin-page-subtitle !mb-0 mt-1">
          Every switch below is still compiled into the app, so flipping one here stages the decision
          rather than changing anyone&rsquo;s phone. It starts taking effect in the update after the
          app learns to read these values. Toggles you set now will apply the moment that ships.
        </p>
      </div>

      {error ? <ErrorBanner className="mb-6">{error}</ErrorBanner> : null}

      {loading ? (
        <SkeletonRows rows={6} />
      ) : (
        <div className="space-y-8">
          {GROUP_ORDER.map((group) => {
            const rows = FLAGS.filter((f) => f.group === group);
            if (rows.length === 0) return null;
            return (
              <section key={group}>
                <h2 className="mb-3 text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">
                  {group}
                </h2>
                <div className="space-y-3">
                  {rows.map((flag) => {
                    const override = overrides[flag.key];
                    const hasOverride = typeof override === 'boolean';
                    const effective = hasOverride ? override : flag.shipped;
                    const differs = hasOverride && override !== flag.shipped;
                    return (
                      <div key={flag.key} className="admin-card">
                        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-semibold">{flag.label}</span>
                              <span
                                className={
                                  effective
                                    ? 'rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-400'
                                    : 'rounded-full bg-zinc-500/15 px-2 py-0.5 text-xs font-medium text-zinc-400'
                                }
                              >
                                {effective ? 'On' : 'Off'}
                              </span>
                              {differs ? (
                                <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-400">
                                  Differs from the shipped app
                                </span>
                              ) : null}
                            </div>
                            <p className="admin-page-subtitle !mb-0 mt-1">{flag.blurb}</p>
                            {flag.caveat ? (
                              <p className="mt-1 text-xs text-amber-400/90">{flag.caveat}</p>
                            ) : null}
                            <p className="mt-2 font-mono text-[11px] text-mutedDim">
                              {flag.key} · app ships {flag.shipped ? 'on' : 'off'} ·{' '}
                              {hasOverride ? `override ${override ? 'on' : 'off'}` : 'no override'}
                            </p>
                          </div>
                          <div className="flex shrink-0 items-center gap-2">
                            <button
                              type="button"
                              className={`admin-btn admin-btn-sm ${effective ? 'admin-btn-secondary' : 'admin-btn-primary'}`}
                              disabled={busyKey === flag.key}
                              onClick={() =>
                                setPending({ key: flag.key, label: flag.label, next: !effective })
                              }
                            >
                              Turn {effective ? 'off' : 'on'}
                            </button>
                            {hasOverride ? (
                              <button
                                type="button"
                                className="admin-btn admin-btn-sm admin-btn-ghost"
                                disabled={busyKey === flag.key}
                                onClick={() => void commit(flag.key, flag.label, null)}
                              >
                                Reset
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <ConfirmModal
        open={pending !== null}
        title={pending ? `Turn ${pending.label} ${pending.next ? 'on' : 'off'}?` : ''}
        message={
          pending
            ? "This is staged until the app reads these values. Nothing on anyone's phone changes right now."
            : ''
        }
        confirmLabel={pending ? `Turn ${pending.next ? 'on' : 'off'}` : 'Confirm'}
        busy={busyKey !== null}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (pending) void commit(pending.key, pending.label, pending.next);
        }}
      />

      {toastNode}
    </div>
  );
}
